import React, { useState } from 'react';
import { BookOpen, Calendar, TrendingUp, Brain, Plus, Sparkles, Search, ChevronDown, ChevronRight } from 'lucide-react';
import { useUserData } from '@/hooks/useUserData';
import { useAuth } from '@/hooks/useAuth';
import { dailyTaskService } from '@/services/dailyTaskService';

const FounderJournal: React.FC = () => {
  const { user } = useAuth();
  const { profile, journalEntries, loading, error, addJournalEntry } = useUserData();

  const [newEntry, setNewEntry] = useState({
    title: '',
    content: '',
    mood: 'focused' as string,
  });

  const [activeView, setActiveView] = useState<'write' | 'entries' | 'analysis'>('write');
  const [expandedEntry, setExpandedEntry] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const moodEmojis = {
    excited: '🚀',
    focused: '🎯',
    challenged: '💪',
    frustrated: '😤',
    optimistic: '🌟'
  };

  const moodColors: Record<string, string> = {
    excited: 'bg-startup-gold/10 border-startup-gold text-startup-gold',
    focused: 'bg-startup-teal/10 border-startup-teal text-startup-teal',
    challenged: 'bg-startup-navy/10 border-startup-navy text-startup-navy',
    frustrated: 'bg-startup-red/10 border-startup-red text-startup-red',
    optimistic: 'bg-startup-peach/20 border-startup-peach text-startup-navy'
  };

  const saveEntry = async () => {
    if (!newEntry.content.trim()) return;
    setIsAnalyzing(true);
    
    const result = await addJournalEntry({
      title: newEntry.title.trim() || 'Untitled Entry',
      content: newEntry.content.trim(),
      mood: newEntry.mood.trim() || undefined,
    });
    
    // Mark daily task as completed if entry was successfully created
    if (result && user?.id) {
      await dailyTaskService.markDailyTaskCompleted(user.id, 'journal_entry');
    }
    
    setNewEntry({ title: '', content: '', mood: 'focused' });
    setIsAnalyzing(false);
    setActiveView('entries');
  };

  const getMoodStats = () => {
    const moodCounts = journalEntries.reduce((acc, entry) => {
      const key = (entry.mood || 'neutral').toString().toLowerCase();
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const total = journalEntries.length || 1;
    return Object.entries(moodCounts).map(([mood, count]) => ({
      mood,
      count,
      percentage: (count / total) * 100
    }));
  };

  // Themes removed for MVP (no tags in live schema)

  // Gate is handled by route-level IdeaValidationGate; avoid in-component gating to prevent stale locks

  if (loading) {
    return (
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-startup-slate rounded w-1/3"></div>
            <div className="h-64 bg-startup-slate rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          <div className="card-startup border-startup-red bg-startup-red/5 text-center py-8">
            <h3 className="text-lg font-semibold text-startup-red mb-2">Error Loading Journal</h3>
            <p className="text-startup-gray">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Do not gate here; IdeaValidationGate already restricts access

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="animate-fade-in">
          <h1 className="text-3xl font-bold text-startup-navy mb-2 flex items-center">
            <BookOpen className="w-8 h-8 mr-3 text-startup-teal" />
            Founder Journal 📝
          </h1>
          <p className="text-startup-gray">
            Reflect on your journey, track your growth, and get AI-powered insights
          </p>
        </div>

        {/* Navigation */}
        <div className="flex space-x-1 bg-white rounded-2xl p-2 shadow-neumorphic animate-fade-in">
          {[
            { id: 'write', label: 'Write Entry', icon: Plus },
            { id: 'entries', label: 'Past Entries', icon: BookOpen },
            { id: 'analysis', label: 'Mood Insights', icon: Brain }
          ].map((tab, index) => (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id as any)}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl transition-all duration-300 flex-1 justify-center ${
                activeView === tab.id
                  ? 'bg-startup-teal text-white shadow-lg'
                  : 'text-startup-gray hover:text-startup-navy hover:bg-startup-slate'
              } animate-fade-in`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <tab.icon className="w-5 h-5" />
              <span className="font-medium">{tab.label}</span>
            </button>
          ))}
        </div>

        {/* Write Entry */}
        {activeView === 'write' && (
          <div className="card-startup animate-fade-in">
            <h2 className="text-2xl font-semibold text-startup-navy mb-6 flex items-center">
              <Plus className="w-6 h-6 mr-3 text-startup-teal" />
              New Journal Entry
            </h2>

            <div className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-startup-navy font-medium mb-2">Entry Title</label>
                <input
                  type="text"
                  value={newEntry.title}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full p-4 border-2 border-startup-teal/20 rounded-xl focus:border-startup-teal focus:outline-none"
                  placeholder="What's on your mind today?"
                />
              </div>

              {/* Content */}
              <div>
                <label className="block text-startup-navy font-medium mb-2">Your Thoughts</label>
                <textarea
                  value={newEntry.content}
                  onChange={(e) => setNewEntry(prev => ({ ...prev, content: e.target.value }))}
                  className="w-full p-4 border-2 border-startup-teal/20 rounded-xl focus:border-startup-teal focus:outline-none resize-none h-48"
                  placeholder="Reflect on your day, challenges, wins, learnings, or anything else on your founder journey..."
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Mood */}
                <div>
                  <label className="block text-startup-navy font-medium mb-3">Current Mood</label>
                  <div className="grid grid-cols-5 gap-2">
                    {Object.entries(moodEmojis).map(([mood, emoji]) => (
                      <button
                        key={mood}
                        onClick={() => setNewEntry(prev => ({ ...prev, mood }))}
                        className={`p-3 rounded-xl border-2 transition-all duration-300 text-center ${
                          newEntry.mood === mood
                            ? moodColors[mood as keyof typeof moodColors] || 'border-startup-gray/20 text-startup-gray'
                            : 'border-startup-gray/20 text-startup-gray hover:border-startup-teal'
                        }`}
                      >
                        <div className="text-2xl mb-1">{emoji}</div>
                        <div className="text-xs font-medium capitalize">{mood}</div>
                      </button>
                    ))}
                  </div>
                </div>

              </div>

              {/* Save Button */}
              <button
                onClick={saveEntry}
                disabled={isAnalyzing || !newEntry.content.trim()}
                className={`w-full py-4 rounded-xl font-semibold text-lg transition-all duration-300 ${
                  isAnalyzing
                    ? 'bg-startup-gray text-white cursor-not-allowed'
                    : 'btn-startup-gold animate-pulse-gold hover:shadow-2xl'
                }`}
              >
                {isAnalyzing ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    <span>Analyzing & Saving...</span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center space-x-2">
                    <Sparkles className="w-5 h-5" />
                    <span>Save & Get AI Insights</span>
                  </div>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Past Entries */}
        {activeView === 'entries' && (
          <div className="space-y-4">
            <div className="card-startup animate-fade-in">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-startup-navy flex items-center">
                  <BookOpen className="w-6 h-6 mr-3 text-startup-teal" />
                  Your Journal Entries
                </h2>
                <div className="flex items-center space-x-2">
                  <Search className="w-5 h-5 text-startup-gray" />
                  <input
                    type="text"
                    placeholder="Search entries..."
                    className="p-2 border border-startup-gray/20 rounded-lg focus:border-startup-teal focus:outline-none"
                  />
                </div>
              </div>

              <div className="space-y-4">
                {journalEntries.map((entry, index) => (
                  <div 
                    key={entry.id}
                    className="border border-startup-gray/20 rounded-xl p-4 hover:shadow-md transition-all duration-300 animate-fade-in"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}
                          className="text-startup-gray hover:text-startup-teal transition-colors"
                        >
                          {expandedEntry === entry.id ? (
                            <ChevronDown className="w-5 h-5" />
                          ) : (
                            <ChevronRight className="w-5 h-5" />
                          )}
                        </button>
                        <h3 className="text-lg font-semibold text-startup-navy">{entry.title || 'Untitled Entry'}</h3>
                      </div>
                      <div className="flex items-center space-x-3">
                        {entry.mood && (
                          <span className={`px-3 py-1 rounded-full text-sm border ${moodColors[entry.mood] || 'border-startup-gray/20 text-startup-gray'}`}>
                            {moodEmojis[entry.mood as keyof typeof moodEmojis] || '📝'} {entry.mood}
                          </span>
                        )}
                        <span className="text-startup-gray text-sm flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {new Date(entry.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>

                    {expandedEntry === entry.id && (
                      <div className="space-y-4 animate-fade-in">
                        <p className="text-startup-navy leading-relaxed">{entry.content}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* AI Analysis */}
        {activeView === 'analysis' && (
          <div className="space-y-6">
            {/* Mood Analytics */}
            <div className="card-startup animate-fade-in">
              <h2 className="text-2xl font-semibold text-startup-navy mb-6 flex items-center">
                <TrendingUp className="w-6 h-6 mr-3 text-startup-teal" />
                Mood Analytics
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-startup-navy mb-4">Mood Distribution</h3>
                  <div className="space-y-3">
                    {getMoodStats().map((stat, index) => (
                      <div key={stat.mood} className="animate-fade-in" style={{ animationDelay: `${index * 0.1}s` }}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="flex items-center space-x-2">
                            <span className="text-lg">{moodEmojis[stat.mood as keyof typeof moodEmojis] || '📝'}</span>
                            <span className="capitalize text-startup-navy font-medium">{stat.mood}</span>
                          </span>
                          <span className="text-startup-gray text-sm">{stat.count} entries</span>
                        </div>
                        <div className="w-full bg-startup-slate rounded-full h-2">
                          <div 
                            className="bg-startup-teal rounded-full h-2 transition-all duration-1000"
                            style={{ width: `${stat.percentage}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>

            {/* Cashpurr AI section removed for MVP */}
          </div>
        )}
      </div>
    </div>
  );
};

export default FounderJournal;
